import { useMutation, useQuery } from "@apollo/client";
import {
  Address,
  AddressDisplay,
  checkHasGenericImplant,
  CustomDateSelection,
  FileType,
  GenericImplantRushAlert,
  ItemType,
  shouldWarnAboutAbutmentSwap,
  UPDATE_DELIVERY_DATE,
  useFiles,
} from "@jasper/shared";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Modal,
  Space,
  Text,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconCloudUpload, IconSquareRoundedPlus } from "@tabler/icons-react";
import * as QRCode from "qrcode";
import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { StepsButtons } from "/@/components/StepsButtons";
import {
  GetOrderByUniqueAttributeQuery,
  GetOrderEstimatedDeliveryDateDocument,
  NotifyPlacedOrderDocument,
  OrderStatus,
  Product,
  UpdateOrderDocument,
} from "/@/generated/graphql";
import { CreateOrderAddProduct } from "/@/pages/orders/create/CreateOrderAddProduct";
import { useCreateOrderStore } from "/@/shared/stores/orders/create-store.store";
import { CreateOrderStep } from "/@/shared/types/orders/createOrder.types";

import {
  CommentType,
  getLastShippingDateFromDeliveryDate,
  getPatientSexLabel,
} from "@jasper/shared";
import { useAuthContext } from "/@/shared/contexts/AuthContext";

type CreateOrderReviewProps = {
  order: NonNullable<
    GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"]
  >;
  refetchOrder: () => Promise<unknown>;
  onValidationComplete?: () => void;
};

import { notifications } from "@mantine/notifications";

import { Dropzone } from "@mantine/dropzone";

import { NotificationEventEnum } from "@jasper/client-models";
import {
  isExtractedFile,
  joinSocketRoom,
  onSocketEvent,
  orderFileRoom,
  ItemsDescription,
  File,
  FileList,
  CreateRemovableItemFullDentureInfo,
  isBeforeDay,
} from "@jasper/shared";
import { ProductFromGetOrderByUniqueAttributeQuery } from "/@/shared/types/products/products.types";
import {
  useCurrentUserNotificationSubscriptions,
  useRequestOrderDesign,
} from "@jasper/use-cases";
import { modals } from "@mantine/modals";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { NotificationSubscriptions } from "../settings/NotificationSettings/NotificationSubscriptions";
import { usePhoneNumberUpdateModal } from "../settings/NotificationSettings/usePhoneNumberUpdateModal";

export const CreateOrderReview: FC<CreateOrderReviewProps> = ({
  order,
  refetchOrder,
  onValidationComplete,
}) => {
  const { user } = useAuthContext();
  const getOrderComment = () => {
    const existingOrderComment = (order?.orderComment ?? []).find(
      comment => (comment.type as unknown as CommentType) === CommentType.ORDER
    );
    return existingOrderComment?.comment ?? "";
  };

  const [orderComment, setOrderComment] = useState("");
  const [isDesignReviewRequested, setIsDesignReviewRequested] = useState(false);
  const [isModalCustomDeliveryDateOpened, setIsModalCustomDeliveryDateOpened] =
    useState(false);

  const canvas = useRef(null);
  const [isPageLoading, setIsPageLoading] = useState(false);

  const {
    data: getOrderEstimatedDeliveryDate,
    loading: getOrderEstimatedDeliveryDateLoading,
    refetch,
  } = useQuery(GetOrderEstimatedDeliveryDateDocument, {
    variables: {
      orderId: order.id,
    },
    fetchPolicy: "network-only",
    pollInterval: 1000 * 60 * 60 * 6, // update every 6 hours
  });

  const [
    isDesignPreviewNotificationModalOpen,
    setIsDesignPreviewNotificationModalOpen,
  ] = useState(false);
  const {
    notificationSubscriptions,
    subscribeToNotification,
    unsubscribeFromNotification,
  } = useCurrentUserNotificationSubscriptions();
  const { promptPhoneNumberUpdateModal } = usePhoneNumberUpdateModal();

  const [updateOrder] = useMutation(UpdateOrderDocument);
  const [notifyPlacedOrder] = useMutation(NotifyPlacedOrderDocument);
  const [updateDeliveryDate] = useMutation(UPDATE_DELIVERY_DATE);
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const [isShowInfoModal, setIsShowInfoModal] = useState(false);
  const [imageList, setImageList] = useState<File[]>([]);
  const [isShowAddProductModal, setIsShowAddProductModal] = useState(false);
  const [updateCreateOrderStep] = useCreateOrderStore(state => [
    state.updateCreateOrderStep,
  ]);
  const [estimatedDeliveryDateString, setEstimatedDeliveryDateString] =
    useState<string | null | undefined>(undefined);
  const [newDeliveryDateSelected, setNewDeliveryDateSelected] =
    useState<Date | null>(
      order.deliveryDate ? new Date(order.deliveryDate) : null
    );
  const optimalDeliveryDate =
    getOrderEstimatedDeliveryDate?.getOrderEstimatedDeliveryDate?.deliveryDate;
  const { requestOrderDesign } = useRequestOrderDesign();
  const orderDesignRequestFeatureFlag = useFeatureFlagEnabled(
    "order_design_request"
  );
  const {
    getPresignedDownloadUrl,
    deleteOneFile,
    uploadFileToS3,
    filesOfOrder,
    refetchFilesOfOrder,
    getFileUrl,
  } = useFiles(order.id);
  const navigate = useNavigate();
  if (canvas.current) {
    const qrLink = `${window.location.origin}/#/order-upload-file/${order.id}/${order.patient?.lastName}/${user?.accessToken}/${FileType.CLIENT_FILE}`;
    QRCode.toCanvas(canvas.current, qrLink.replace(" ", "%20"), error => {
      if (error) console.error(error);
    });
  }

  const refreshImageList = async (data: any) => {
    return await Promise.all(
      data.map(async (obj: any) => {
        const path = (await getFileUrl(obj.key)) ?? null;
        return {
          ...obj,
          path: path,
        };
      })
    );
  };

  useEffect(() => {
    const leaveRoom = joinSocketRoom(orderFileRoom(order.id));
    const unsubscribe = onSocketEvent("clientOrderFile", () => {
      refetchFilesOfOrder();
    });

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetch();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      unsubscribe();
      leaveRoom();
    };
  }, []);

  useEffect(() => {
    setOrderComment(getOrderComment());
  }, [order]);

  useEffect(() => {
    if (filesOfOrder?.getFilesOfOrder) {
      refreshImageList(filesOfOrder?.getFilesOfOrder).then(a =>
        setImageList(a)
      );
    }
  }, [filesOfOrder?.getFilesOfOrder]);

  useEffect(() => {
    if (getOrderEstimatedDeliveryDateLoading) {
      return;
    }
    if (
      getOrderEstimatedDeliveryDate?.getOrderEstimatedDeliveryDate?.deliveryDate
    ) {
      const estimatedDeliveryDate = new Date(
        getOrderEstimatedDeliveryDate.getOrderEstimatedDeliveryDate
          ?.deliveryDate
      );
      if (newDeliveryDateSelected) {
        setEstimatedDeliveryDateString(
          (newDeliveryDateSelected as Date).toLocaleString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            weekday: "long",
          })
        );
      } else {
        setEstimatedDeliveryDateString(
          estimatedDeliveryDate.toLocaleString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            weekday: "long",
          })
        );
      }
    } else {
      setEstimatedDeliveryDateString(null);
    }
  }, [getOrderEstimatedDeliveryDate, getOrderEstimatedDeliveryDateLoading]);

  const location = useLocation();
  const { state } = location;

  useEffect(() => {
    if (state?.openAddProduct) {
      setIsShowAddProductModal(true);
      navigate(location.pathname, {
        replace: true,
        state: { id: state.id, productType: state.productType },
      });
    }
  }, [state?.openAddProduct]);

  const getCommentBlock = () => {
    const existingOrderComment = (order?.orderComment ?? []).find(
      comment => (comment.type as unknown as CommentType) === CommentType.ORDER
    );
    let commentBlock = undefined;
    if (existingOrderComment) {
      commentBlock = {
        update: [
          {
            data: {
              comment: {
                set: orderComment,
              },
            },
            where: {
              id: existingOrderComment?.id,
            },
          },
        ],
      };
    } else if (orderComment) {
      commentBlock = {
        create: [
          {
            comment: orderComment,
          },
        ],
      };
    }
    return commentBlock;
  };

  const saveOrderComment = async () => {
    try {
      await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            comment: {
              set: orderComment ?? null,
            },
            orderComment: getCommentBlock(),
          },
        },
      });
      refetchOrder();
    } catch (e) {
      console.error(e);
    }
  };

  const onSubmit = async () => {
    try {
      setIsPageLoading(true);
      const toSaveDeliveryDate = newDeliveryDateSelected
        ? new Date(newDeliveryDateSelected)
        : undefined;
      if (toSaveDeliveryDate) toSaveDeliveryDate.setHours(12);
      const res = await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            confirmedAt: {
              set: Date.now(),
            },
            comment: {
              set: orderComment ?? null,
            },
            status: {
              set: OrderStatus.Confirmed,
            },
            orderComment: getCommentBlock(),
            deliveryDate: toSaveDeliveryDate
              ? {
                  set: toSaveDeliveryDate,
                }
              : getOrderEstimatedDeliveryDate?.getOrderEstimatedDeliveryDate
                    ?.deliveryDate
                ? {
                    set: getOrderEstimatedDeliveryDate
                      ?.getOrderEstimatedDeliveryDate?.deliveryDate,
                  }
                : undefined,
            lastShippingDate: toSaveDeliveryDate
              ? {
                  set: getLastShippingDateFromDeliveryDate(toSaveDeliveryDate),
                }
              : getOrderEstimatedDeliveryDate?.getOrderEstimatedDeliveryDate
                ? {
                    set: getLastShippingDateFromDeliveryDate(
                      getOrderEstimatedDeliveryDate
                        ?.getOrderEstimatedDeliveryDate?.deliveryDate
                    ),
                  }
                : undefined,
            orderBilling: {
              create: [
                {
                  user: {
                    connect: {
                      id: order.user.id,
                    },
                  },
                },
              ],
            },
          },
        },
      });
      if (res.data?.updateOrder?.deliveryDate) {
        const estimatedDeliveryDate = new Date(
          res.data.updateOrder.deliveryDate
        );
        setEstimatedDeliveryDateString(
          estimatedDeliveryDate.toLocaleString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            weekday: "long",
          })
        );
      }

      if (isDesignReviewRequested) {
        await requestOrderDesign(order.id).catch(err => {
          console.error("Error while requesting order design", err);
        });

        if (
          !Object.values(
            notificationSubscriptions?.ORDER_DESIGN_OPENED ?? {}
          ).some(value => value)
        ) {
          setIsDesignPreviewNotificationModalOpen(true);
        }
      }

      setIsPageLoading(false);
      setIsShowConfirmModal(true);
      if (checkHasGenericImplant(order)) {
        await updateDeliveryDate({
          variables: {
            orderId: order.id,
            newDeliveryDate: toSaveDeliveryDate,
            shouldUpdateAbutmentType: true,
          },
        });
      }
      await notifyPlacedOrder({
        variables: {
          orderId: order.id,
        },
      });
      setIsPageLoading(false);
    } catch (e) {
      console.error(e);
      setIsPageLoading(false);
    }
  };

  const onSubmitUpdateDeliveryDate = async (newDeliveryDate: Date) => {
    setIsPageLoading(true);
    const toSaveDeliveryDate = new Date(newDeliveryDate);
    if (toSaveDeliveryDate) toSaveDeliveryDate.setHours(12);
    const req = await updateDeliveryDate({
      variables: {
        orderId: order.id,
        newDeliveryDate: toSaveDeliveryDate,
        shouldUpdateAbutmentType: false,
      },
    });
    setIsModalCustomDeliveryDateOpened(false);
    if (req?.data?.updateOrderDeliveryDate) {
      refetch();
      refetchOrder();
      notifications.show({
        color: "green",
        title: "Date de livraison modifiée",
        message: "",
      });
      setNewDeliveryDateSelected(newDeliveryDate);
      setIsPageLoading(false);
    } else {
      notifications.show({
        color: "red",
        title: "Erreur lors de la demande de modification",
        message: "",
      });
      setIsPageLoading(false);
    }
  };

  if (
    !order.patient ||
    !order.user?.userGroup?.addresses?.[0] ||
    !order.products ||
    order.products.length < 1
  ) {
    return <div>Erreur données</div>;
  }

  if (
    getOrderEstimatedDeliveryDateLoading &&
    estimatedDeliveryDateString === undefined
  ) {
    return <Box></Box>;
  }

  const patientBirthday = order.patient.birthdate
    ? new Date(order.patient.birthdate as string).toLocaleDateString("fr-FR")
    : null;

  const ERROR_MESSAGE_DESIGN_RUSH =
    "La demande d'un design ne permet pas de bénéficier d'un délai réduit";

  return (
    <>
      <Modal
        title="Voulez vous recevoir une notification lorsque le design de votre commande sera prêt pour revue ?"
        centered
        opened={isDesignPreviewNotificationModalOpen}
        onClose={() => setIsDesignPreviewNotificationModalOpen(false)}
        closeOnEscape
      >
        <NotificationSubscriptions
          flex={4}
          subscriptions={notificationSubscriptions?.ORDER_DESIGN_OPENED ?? {}}
          onChange={async (channel, value) => {
            if (value) {
              if (
                channel === "SMS" &&
                (!user?.phoneNumber || user?.phoneNumber === "NOT_PROVIDED")
              ) {
                const isPhoneNumberUpdated =
                  await promptPhoneNumberUpdateModal();
                if (!isPhoneNumberUpdated) {
                  return;
                }
              }

              subscribeToNotification({
                event: NotificationEventEnum.ORDER_DESIGN_OPENED,
                channel,
              });
            } else {
              unsubscribeFromNotification({
                event: NotificationEventEnum.ORDER_DESIGN_OPENED,
                channel,
              });
            }
          }}
        />
      </Modal>
      <Box
        p="lg"
        style={() => ({
          display: "flex",
          flexDirection: "column",
          height: "100%",
        })}
      >
        <LoadingOverlay
          visible={isPageLoading}
          zIndex={1000}
          overlayProps={{
            radius: "sm",
            blur: 2,
          }}
        >
          <Loader />
        </LoadingOverlay>
        <Modal
          opened={isModalCustomDeliveryDateOpened}
          size="auto"
          onClose={() => setIsModalCustomDeliveryDateOpened(false)}
          title={
            checkHasGenericImplant(order) ? (
              <div style={{ width: "32rem" }}>
                <GenericImplantRushAlert />
              </div>
            ) : (
              ""
            )
          }
        >
          <CustomDateSelection
            selectDate={newDeliveryDate =>
              onSubmitUpdateDeliveryDate(newDeliveryDate)
            }
            optimalDeliveryDate={
              new Date(
                getOrderEstimatedDeliveryDate?.getOrderEstimatedDeliveryDate
                  ?.deliveryDate
              )
            }
            bankHolidays={
              getOrderEstimatedDeliveryDate?.getOrderEstimatedDeliveryDate
                ?.bankHolidays
            }
            minimalRushDeliveryDate={
              getOrderEstimatedDeliveryDate?.getOrderEstimatedDeliveryDate
                ?.minimalRushDeliveryDate
            }
            selectedDate={newDeliveryDateSelected}
          />
        </Modal>
        {!isShowAddProductModal && (
          <>
            <Title>Complétez et validez votre commande</Title>
            <Space h="md" />
            <Title
              style={theme => ({
                color: theme.colors.primary[5],
              })}
              order={3}
            >
              Date de livraison estimée :{" "}
              {(
                newDeliveryDateSelected ?? estimatedDeliveryDateString
              )?.toLocaleString("fr-FR", {
                year: "numeric",
                month: "long",
                day: "numeric",
                weekday: "long",
              }) ??
                "En cours de calcul. Elle vous sera communiquée prochainement"}
              {user?.customDeliveryDateEnabled && (
                <Tooltip
                  disabled={!isDesignReviewRequested}
                  label={ERROR_MESSAGE_DESIGN_RUSH}
                >
                  <Button
                    style={{
                      marginLeft: "2rem",
                    }}
                    onClick={async () => {
                      await saveOrderComment();
                      setIsModalCustomDeliveryDateOpened(true);
                    }}
                    disabled={isDesignReviewRequested}
                  >
                    Changer la date de livraison
                  </Button>
                </Tooltip>
              )}
            </Title>
            <Space h="md" />
            <Grid gap="md">
              <Grid.Col span={6}>
                <Container
                  p="xl"
                  style={theme => ({
                    borderRadius: "10px",
                    backgroundColor: theme.colors.primaryLight[9],
                    height: "100%",
                  })}
                >
                  <Grid gap="md">
                    <Grid.Col span={6}>
                      <Box>
                        <Title order={4}>Praticien</Title>
                        <Text>
                          {order.user.firstName} {order.user.lastName}
                        </Text>
                        <Text>
                          <AddressDisplay
                            address={
                              order?.user?.userGroup?.addresses?.[0] as Address
                            }
                          />
                        </Text>
                      </Box>
                      <Space h="xl" />
                      <Box>
                        <Title order={4}>Patient</Title>
                        <Text>
                          {order.patient.firstName} {order.patient.lastName}
                        </Text>
                        {patientBirthday && <Text>{patientBirthday}</Text>}
                        {!order.patient.sex && (
                          <Text>{getPatientSexLabel(order.patient.sex)}</Text>
                        )}
                      </Box>
                    </Grid.Col>
                    <Grid.Col
                      span={6}
                      style={{
                        margin: "auto 0",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          width: "fit-content",
                          float: "right",
                          marginBottom: "10px",
                        }}
                      >
                        <p
                          style={{
                            textAlign: "center",
                            margin: "0",
                          }}
                        >
                          Ajoutez des photos
                          <br />
                          via ce QR Code
                        </p>
                        <canvas
                          style={{
                            maxWidth: "170px",
                            maxHeight: "170px",
                            margin: "auto",
                          }}
                          ref={canvas}
                        />
                        <p
                          style={{
                            textAlign: "center",
                            margin: "5px",
                          }}
                        >
                          OU
                        </p>

                        <Dropzone
                          accept={{
                            "image/*": [],
                            "application/dicom": [".DCM", ".dcm"],
                            "model/stl": [".stl", ".STL"],
                          }}
                          onDrop={async files => {
                            await uploadFileToS3(
                              [...files],
                              FileType.ORDER_FILE
                            );
                            refetch();
                            refetchFilesOfOrder();
                          }}
                          style={{
                            border: "2px dashed gray",
                            borderRadius: "5px",
                            padding: "5px",
                            cursor: "pointer",
                          }}
                        >
                          <Flex justify="center" align="center">
                            <IconCloudUpload />
                            <Box ml="sm">
                              <span
                                style={{
                                  fontWeight: "bold",
                                  textWrap: "wrap",
                                  fontSize: "14px",
                                }}
                              >
                                {" "}
                                {"Ajouter un fichier à votre commande"}
                              </span>
                            </Box>
                          </Flex>
                        </Dropzone>
                      </div>
                    </Grid.Col>
                  </Grid>
                  <Box>
                    {imageList && imageList.length > 0 && (
                      <Box mt={10} mb={10}>
                        <FileList
                          files={[
                            ...imageList.filter(
                              file =>
                                !file.key.includes(".3OXZ") &&
                                !isExtractedFile(file.key) &&
                                !file.key.includes(
                                  "-traçabilité-praticien.pdf"
                                ) &&
                                !file.key.includes("-traçabilité-patient.pdf")
                            ),
                          ]}
                          deleteOneFile={deleteOneFile}
                          downloadOneFile={getPresignedDownloadUrl}
                        />
                      </Box>
                    )}
                    <Textarea
                      value={orderComment}
                      color="primary"
                      placeholder="Un commentaire sur la commande ? (livraison, délais, etc.)"
                      onChange={event =>
                        setOrderComment(event.currentTarget.value)
                      }
                    />
                  </Box>
                  {orderDesignRequestFeatureFlag && (
                    <Checkbox
                      label="Demander un design avant fabrication"
                      pt="xs"
                      size="md"
                      checked={isDesignReviewRequested}
                      onChange={() => {
                        const defaultDeliveryDate = new Date(
                          getOrderEstimatedDeliveryDate
                            ?.getOrderEstimatedDeliveryDate?.deliveryDate
                        );
                        if (
                          newDeliveryDateSelected &&
                          isBeforeDay(
                            new Date(newDeliveryDateSelected),
                            defaultDeliveryDate
                          )
                        ) {
                          modals.openConfirmModal({
                            title: "Demande de confirmation",
                            children: <Text>{ERROR_MESSAGE_DESIGN_RUSH}</Text>,
                            labels: { confirm: "Confirmer", cancel: "Annuler" },
                            onConfirm: () => {
                              onSubmitUpdateDeliveryDate(defaultDeliveryDate);
                              setIsDesignReviewRequested(
                                !isDesignReviewRequested
                              );
                            },
                          });
                        } else {
                          setIsDesignReviewRequested(!isDesignReviewRequested);
                        }
                      }}
                    />
                  )}
                </Container>
              </Grid.Col>
              <Grid.Col span={6}>
                <Container
                  p="xl"
                  style={theme => ({
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    borderRadius: "10px",
                    backgroundColor: theme.colors.primaryLight[9],
                    height: "100%",
                  })}
                >
                  <Box
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      {order.products.map(product => (
                        <ItemsDescription
                          key={product.id}
                          product={product}
                          order={order}
                          shouldTranslate={false}
                        />
                      ))}
                    </div>
                    {shouldWarnAboutAbutmentSwap(
                      order,
                      newDeliveryDateSelected,
                      optimalDeliveryDate
                    ) && <GenericImplantRushAlert />}
                  </Box>
                  <Button
                    h={60}
                    leftSection={
                      <IconSquareRoundedPlus size={24} stroke={2.5} />
                    }
                    fw={600}
                    onClick={async () => {
                      await saveOrderComment();
                      setIsShowAddProductModal(true);
                    }}
                  >
                    {"Ajouter un produit"}
                  </Button>
                </Container>
              </Grid.Col>
            </Grid>
            <Space h="xl" />
            <StepsButtons
              handleSubmit={() => onSubmit()}
              applyBeforePreviousStep={async () => await saveOrderComment()}
              setPreviousAction={() => {
                updateCreateOrderStep(CreateOrderStep.Product);
              }}
              previousStep={CreateOrderStep.Product}
              nextLabel="Valider ma commande"
              isValidateStyle={true}
            />
            {!isDesignPreviewNotificationModalOpen && isShowConfirmModal && (
              <Modal
                opened={isShowConfirmModal}
                onClose={() => {
                  setIsShowConfirmModal(false);
                  if (onValidationComplete) {
                    onValidationComplete();
                  } else {
                    navigate(`/order/${order.id}`);
                  }
                }}
                size="xl"
                withCloseButton={false}
                centered
                overlayProps={{
                  opacity: 0.6,
                  blur: 17,
                }}
              >
                <Container
                  p="lg"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignContent: "center",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Title
                    order={2}
                    style={theme => ({
                      color: theme.colors.primary[3],
                    })}
                  >
                    Commande passée avec succès !
                  </Title>
                  <Space h="lg" />
                  <Title
                    order={2}
                    style={theme => ({
                      color: theme.colors.green[9],
                    })}
                  >
                    {estimatedDeliveryDateString &&
                    estimatedDeliveryDateString !== ""
                      ? `Date de livraison estimée: ${
                          estimatedDeliveryDateString ??
                          "Communiquée prochainement sur la page de la commande"
                        }`
                      : ""}
                  </Title>
                  <Space h="md" />
                  <Text
                    style={{
                      textAlign: "center",
                    }}
                  >
                    {
                      "Vous pouvez suivre votre commande depuis votre dashboard, ou en cliquant ci-dessous"
                    }
                  </Text>
                  <Space h="xl" />
                  <Button
                    onClick={() => {
                      setIsShowConfirmModal(false);
                      const isFullDentureOrder = order?.products?.some(
                        (product: Product) =>
                          product.removableItem?.some((item: any) => {
                            return item.itemType === ItemType.FULL_DENTURE;
                          })
                      );

                      if (isFullDentureOrder) {
                        setIsShowInfoModal(true);
                      } else if (onValidationComplete) {
                        onValidationComplete();
                      } else {
                        navigate(`/order/${order.id}`);
                      }
                    }}
                    data-testid="button-show-my-order"
                  >
                    Voir ma commande
                  </Button>
                </Container>
              </Modal>
            )}
          </>
        )}
        {isShowAddProductModal && (
          <Modal
            size="100%"
            opened={isShowAddProductModal}
            onClose={() => {
              setIsShowAddProductModal(false);
            }}
          >
            <CreateOrderAddProduct
              closeModal={() => setIsShowAddProductModal(false)}
              refetchOrder={refetchOrder}
            />
          </Modal>
        )}
        {isShowInfoModal &&
          order &&
          (() => {
            const fullDentureProduct = order.products?.find((p: Product) =>
              p.removableItem?.some(
                (item: any) => item.itemType === ItemType.FULL_DENTURE
              )
            );

            return (
              <Modal
                size="lg"
                opened={isShowInfoModal}
                onClose={() => {
                  setIsShowInfoModal(false);
                  if (onValidationComplete) {
                    onValidationComplete();
                  } else {
                    navigate(`/order/${order.id}`);
                  }
                }}
              >
                <CreateRemovableItemFullDentureInfo
                  product={
                    {
                      ...fullDentureProduct,
                      orderId: order.id,
                    } as ProductFromGetOrderByUniqueAttributeQuery
                  }
                  orderId={order.id}
                  pickupCarrier={user?.userGroup?.pickupCarrier}
                />
              </Modal>
            );
          })()}
      </Box>
    </>
  );
};

type DropzoneChildrenProps = Record<string, never>;

export const DropzoneChildren: FC<DropzoneChildrenProps> = () => {
  return (
    <Box
      style={() => ({
        display: "flex",
      })}
    >
      <IconCloudUpload />
      <Box ml="sm">
        <Text size="sm" style={{ fontWeight: "bold" }}>
          {" "}
          {"Ajoutez ou glissez des fichiers à votre commande"}
        </Text>
      </Box>
    </Box>
  );
};
